import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../api";
import { Card, ErrorNotice, Loading } from "./Ui";
import { formatDate } from "../utils";
import type { User } from "../types";
import { usePermissions } from "../permissions";

type Capacity = { department: string; status: string; notes: string };
type Review = {
  version: number;
  notes: string;
  capacity: Capacity[];
  updatedAt: string | null;
};
export function PortfolioReview({ user }: { user: User }) {
  const permissions = usePermissions();
  const now = new Date();
  const [quarter, setQuarter] = useState(
    `Q${Math.floor(now.getMonth() / 3) + 1} ${now.getFullYear()}`,
  );
  const api = useApi();
  const query = useQuery({
    queryKey: ["portfolio-review", quarter],
    queryFn: () =>
      api.get<Review>(`/portfolio/review/${encodeURIComponent(quarter)}`),
  });
  return (
    <Card className="span-2">
      <div className="card-header">
        <h2>Strategic notes</h2>
        <label>
          Quarter{" "}
          <select value={quarter} onChange={(e) => setQuarter(e.target.value)}>
            {Array.from(
              { length: 5 },
              (_, y) => now.getFullYear() - 2 + y,
            ).flatMap((year) =>
              [1, 2, 3, 4].map((q) => (
                <option key={`${year}-${q}`}>{`Q${q} ${year}`}</option>
              )),
            )}
          </select>
        </label>
      </div>
      <p className="section-help">
        Notes are visible to users with Executive access; exclude
        confidential initiative details.
      </p>
      {user.role === "admin" && (
        <p>
          <Link to="/capacity">
            View capacity health and delivery planning
          </Link>
        </p>
      )}
      {query.isPending ? (
        <Loading />
      ) : query.isError ? (
        <ErrorNotice error={query.error} />
      ) : (
        <ReviewForm
          key={`${quarter}-${query.data.version}`}
          quarter={quarter}
          review={query.data}
          editable={permissions.businessCan("edit", user.role === "admin")}
        />
      )}
    </Card>
  );
}
function ReviewForm({
  quarter,
  review,
  editable,
}: {
  quarter: string;
  review: Review;
  editable: boolean;
}) {
  const api = useApi();
  const client = useQueryClient();
  const [notes, setNotes] = useState(review.notes);
  const [reason, setReason] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      api.put(`/portfolio/review/${encodeURIComponent(quarter)}`, {
        notes,
        capacity: review.capacity,
        expectedVersion: review.version,
        changeReason: reason,
      }),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ["portfolio-review", quarter] }),
  });
  return (
    <form
      className="portfolio-form"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <label>
        Strategic notes
        <textarea
          value={notes}
          readOnly={!editable}
          maxLength={12000}
          rows={4}
          placeholder="Decisions needed, sequencing constraints and management actions"
          onChange={(e) => setNotes(e.target.value)}
        />
      </label>
      <p className="muted">
        Last saved: {formatDate(review.updatedAt)} · Version {review.version}
      </p>
      {editable && (
        <>
          <label>
            Change reason
            <input
              required
              minLength={3}
              maxLength={1000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <button
            className="btn-primary"
            disabled={mutation.isPending || reason.trim().length < 3}
          >
            {mutation.isPending ? "Saving…" : "Save quarterly review"}
          </button>
        </>
      )}
      {mutation.isError && <ErrorNotice error={mutation.error} />}{" "}
      {mutation.isSuccess && <p role="status">Quarterly review saved.</p>}
    </form>
  );
}

type Assignment = { department: string; leader: string; deputy: string | null };
type Assignments = { version: number; assignments: Assignment[] };
export function DepartmentAssignments() {
  const api = useApi();
  const query = useQuery({
    queryKey: ["department-assignments"],
    queryFn: () => api.get<Assignments>("/portfolio/departments"),
  });
  return (
    <Card>
      <h2>Department leads and deputies</h2>
      <p className="section-help">
        Assign two separate authorised accounts for continuity. Assignments do
        not grant administrator privileges or access to confidential
        initiatives. Add a second S&E administrator using the existing
        Administrator access level.
      </p>
      {query.isPending ? (
        <Loading />
      ) : query.isError ? (
        <ErrorNotice error={query.error} />
      ) : (
        <AssignmentsForm key={query.data.version} record={query.data} />
      )}
    </Card>
  );
}
function AssignmentsForm({ record }: { record: Assignments }) {
  const api = useApi();
  const client = useQueryClient();
  const [rows, setRows] = useState(record.assignments);
  const [reason, setReason] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      api.put("/portfolio/departments", {
        assignments: rows.map((r) => ({ ...r, deputy: r.deputy || null })),
        expectedVersion: record.version,
        changeReason: reason,
      }),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ["department-assignments"] }),
  });
  const change = (index: number, key: keyof Assignment, value: string) =>
    setRows(
      rows.map((row, i) => (i === index ? { ...row, [key]: value } : row)),
    );
  return (
    <form
      className="portfolio-form"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <div className="table-wrap">
        <table className="initiative-table">
          <thead>
            <tr>
              <th>Department</th>
              <th>Leader email</th>
              <th>Deputy email</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>
                  <input
                    aria-label={`Department ${index + 1}`}
                    required
                    minLength={2}
                    maxLength={100}
                    value={row.department}
                    onChange={(e) =>
                      change(index, "department", e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    aria-label={`Leader ${index + 1}`}
                    type="email"
                    required
                    value={row.leader}
                    onChange={(e) => change(index, "leader", e.target.value)}
                  />
                </td>
                <td>
                  <input
                    aria-label={`Deputy ${index + 1}`}
                    type="email"
                    value={row.deputy ?? ""}
                    onChange={(e) => change(index, "deputy", e.target.value)}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="btn-outline"
                    onClick={() => setRows(rows.filter((_, i) => i !== index))}
                  >
                    Remove assignment
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        className="btn-outline"
        disabled={rows.length >= 50}
        onClick={() =>
          setRows([...rows, { department: "", leader: "", deputy: null }])
        }
      >
        Add department
      </button>
      <label>
        Change reason
        <input
          required
          minLength={3}
          maxLength={1000}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <button
        className="btn-primary"
        disabled={mutation.isPending || reason.trim().length < 3}
      >
        {mutation.isPending ? "Saving…" : "Save assignments"}
      </button>
      {mutation.isError && <ErrorNotice error={mutation.error} />}
    </form>
  );
}
